import { TextField } from '@mui/material';

import { CoreBox, CoreFormControl, CoreTypography } from '@youscience/core';

import { RecommenderExtendedProfile } from '@youscience/commonapp-service-common';

import useRecommenderStore from '@store/RecommenderStore';
import useRecommenderProfileStore, { SectionName } from '@store/RecommenderProfileStore';

import SectionAccordion from '@components/SectionAccordion';

import ContinueButton from '@components/ContinueButton';

import { StyledCard, StyledFieldsContainer } from '../../../Card.styles';
import { SchoolDetailsCounselorFields, SchoolDetailsProps } from '../../types';
import useFormSection from '@pages/RecommenderSpace/hooks/useFormSection';
import { countWordsForLongTextFields } from '@pages/StudentDetailsPage/components/common/formUtils';

export const SchoolDetailsCounselor = ({
  onExpand,
  onSaveAndContinue,
  isExtended,
  isSaved,
}: SchoolDetailsProps<SchoolDetailsCounselorFields>) => {
  const { recommenderProfile } = useRecommenderStore((store) => store);

  const {
    initialState: { schoolDetailsCounselor: initialState },
    formState: { schoolDetailsCounselor: formState },
  } = useRecommenderProfileStore((store) => store);

  const initializeValues = () => {
    const communityDisruption = recommenderProfile?.schoolProfile?.schoolDetails?.communityDisruption ?? '';

    return {
      communityDisruption,
    };
  };

  const { handleTextFieldChange } = useFormSection<SchoolDetailsCounselorFields>({
    initialState,
    formState,
    initializeValues,
    sectionName: SectionName.SchoolDetailsCounselor,
  });

  const highSchool = recommenderProfile?.highSchool ?? ({} as NonNullable<RecommenderExtendedProfile['highSchool']>);

  return (
    <SectionAccordion
      title={'School Details'}
      isExpanded={isExtended}
      onChange={onExpand}
      isChanged={formState.hasChanged}
    >
      {Object.keys(formState.data).length === 0 ? null : (
        <StyledCard>
          <StyledFieldsContainer>
            <CoreBox>
              <CoreTypography>{highSchool.schoolName}</CoreTypography>
              <CoreTypography>{highSchool.address1}</CoreTypography>
              <CoreTypography>
                {highSchool.city ? `${highSchool.city}, ` : ''}
                {highSchool.state} {highSchool.zip}{' '}
              </CoreTypography>
              <CoreTypography>{highSchool.schoolType}</CoreTypography>
              <CoreTypography>CEEB Code: {highSchool.schoolCode}</CoreTypography>
            </CoreBox>

            <CoreBox>
              <CoreTypography>
                Your school may have made adjustments due to community disruptions such as COVID–19 or natural
                disasters. If you have not already addressed those changes in your uploaded school profile or elsewhere,
                you can elaborate here. Colleges are especially interested in understanding changes to:
              </CoreTypography>

              <ul>
                <CoreTypography component="li">Grading scales and policies</CoreTypography>
                <CoreTypography component="li">Graduation requirements</CoreTypography>
                <CoreTypography component="li">Instructional methods</CoreTypography>
                <CoreTypography component="li">Schedules and course offerings</CoreTypography>
                <CoreTypography component="li">Testing requirements</CoreTypography>
                <CoreTypography component="li">Your academic calendar</CoreTypography>
                <CoreTypography component="li">Other extenuating circumstances</CoreTypography>
              </ul>

              <CoreTypography>
                Your students will have a similar space in their application to share how these events have affected
                them personally.
              </CoreTypography>
            </CoreBox>
            <CoreFormControl fullWidth>
              <TextField
                label="Additional details"
                name="communityDisruption"
                inputProps={{ maxLength: 500 }}
                onChange={handleTextFieldChange}
                helperText={`${countWordsForLongTextFields(formState.data.communityDisruption)}/500`}
                value={formState.data.communityDisruption}
                multiline
                rows={3}
              />
            </CoreFormControl>

            <ContinueButton onContinue={onSaveAndContinue} />
          </StyledFieldsContainer>
        </StyledCard>
      )}
    </SectionAccordion>
  );
};
